import { useCallback } from 'react'
import ReactNativeBlobUtil from 'react-native-blob-util'
import { Bugsnag } from '@src/config'
import { CreateDirectUploadMutation } from '@src/graphql/createDirectUpload.generated'
import { DirectUploadReason, FileMetadata } from '@src/types'
import { useDispatchAsync } from '@src/utils'
import hexToBase64 from '@src/utils/hexToBase64'
import { ReactNativeFile } from '@src/utils/image'

export const useMealPhotoUpload = () => {
  const dispatchAsync = useDispatchAsync()

  const uploadMealPhoto = useCallback(
    async (file: ReactNativeFile): Promise<string | null> => {
      try {
        // Strip a `file://` or `file:` scheme so react-native-blob-util gets a plain path.
        const filePath = file.uri.replace(/^file:(\/\/)?/, '')

        const [photoHash, { size }] = await Promise.all([
          ReactNativeBlobUtil.fs.hash(filePath, 'md5'),
          ReactNativeBlobUtil.fs.stat(filePath),
        ])

        const upload = await dispatchAsync<CreateDirectUploadMutation['createDirectUpload']>({
          type: 'app/createDirectUpload',
          payload: {
            file: {
              checksum: hexToBase64(photoHash),
              filename: file.name,
              contentType: file.type,
              byteSize: size,
            } as FileMetadata,
            reason: DirectUploadReason.MealPhoto,
          },
        })

        const response = await ReactNativeBlobUtil.fetch(
          'PUT',
          upload.url,
          upload.headers.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}),
          ReactNativeBlobUtil.wrap(filePath),
        )

        // ReactNativeBlobUtil.fetch resolves even on a non-2xx response, so check the
        // status explicitly — otherwise we'd return a signedId for a blob that never
        // landed in S3 and skip the inline-upload fallback.
        const { status } = response.respInfo
        if (status < 200 || status >= 300) {
          throw new Error(`Meal photo S3 upload failed with status ${status}`)
        }

        return upload.signedId
      } catch (error: any) {
        Bugsnag.notify(error)
        return null
      }
    },
    [dispatchAsync],
  )

  return { uploadMealPhoto }
}
